// src/store/tenantSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../api/firebase";
import {
  saveTenantConfig,
  saveTenantCatalog,
} from "../features/portal/tenant-management/tenantJsonEditorSlice";

const initialState = {
  loading: false,
  error: null,
  id: null,
  tenantData: null,
  config: {},
  catalog: {
    status: "idle",
    error: null,
    tree: {},
    rootNodes: [],
  },
};

export const fetchTenantData = createAsyncThunk(
  "tenant/fetchData",
  async (tenantId, { rejectWithValue }) => {
    if (!tenantId) return rejectWithValue("No Tenant ID");
    try {
      const docRef = doc(db, "tenants", tenantId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const { catalog, ...baseConfig } = data;
        return { id: tenantId, config: baseConfig };
      } else {
        return rejectWithValue(`Tenant '${tenantId}' not found.`);
      }
    } catch (e) {
      return rejectWithValue(e.message);
    }
  },
);

export const fetchInitialCatalogTree = createAsyncThunk(
  "tenant/fetchInitialCatalogTree",
  async (tenantId, { getState, rejectWithValue }) => {
    const { config } = getState().tenant;
    const initialDepth = config.catalogConfig?.initialLoadDepth || 1;

    try {
      const fetchCollection = async (path) => {
        const collectionRef = collection(db, ...path);
        const snapshot = await getDocs(collectionRef);
        return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      };

      // 1. Fetch Root Nodes (Items or Categories)
      let rootNodes = await fetchCollection(["tenants", tenantId, "catalog"]);

      // Ordenamiento básico
      rootNodes.sort((a, b) => (a.order || 0) - (b.order || 0));

      // 2. Fetch Children ONLY for Categories with slug (Loretta Architecture)
      if (initialDepth > 1 && rootNodes.length > 0) {
        for (const node of rootNodes) {
          // Solo intentamos buscar hijos si tiene slug (es una Categoría contenedora)
          if (node.type === "category" && node.slug) {
            const itemsPath = [
              "tenants",
              tenantId,
              "catalog",
              node.slug,
              "items",
            ];
            const items = await fetchCollection(itemsPath);
            if (items.length > 0) {
              node.items = items.sort(
                (a, b) => (a.order || 0) - (b.order || 0),
              );
            }

            // Subcategories (si aplica)
            const subPath = [
              "tenants",
              tenantId,
              "catalog",
              node.slug,
              "subcategories",
            ];
            const subs = await fetchCollection(subPath);
            if (subs.length > 0) {
              node.subcategories = subs;
            }
          }
          // Si no tiene slug o es type='item', lo tratamos como hoja (Congroup Architecture)
        }
      }
      return { rootNodes };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

const tenantSlice = createSlice({
  name: "tenant",
  initialState,
  reducers: {
    resetTenantState: (state) => {
      state.loading = true;
      state.error = null;
      state.id = null;
      state.tenantData = null;
      state.config = {};
      state.catalog = initialState.catalog;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTenantData.fulfilled, (state, action) => {
        state.loading = false;
        state.id = action.payload.id;
        state.config = action.payload.config;
        state.tenantData = action.payload.config;
      })
      .addCase(fetchInitialCatalogTree.fulfilled, (state, action) => {
        state.catalog.status = "succeeded";
        state.catalog.rootNodes = action.payload.rootNodes;
      })
      .addCase(saveTenantConfig.fulfilled, (state, action) => {
        const { catalog, ...baseConfig } = action.payload;
        state.config = baseConfig;
        state.tenantData = baseConfig;
      })
      .addCase(saveTenantCatalog.fulfilled, (state) => {
        state.catalog.status = "idle"; // Invalidate cache
      });
    // ... (otros casos de error/loading estándar se mantienen implícitos)
  },
});

export const { resetTenantState } = tenantSlice.actions;
export default tenantSlice.reducer;
